import { Component, ComponentRef, Inject, TemplateRef, ViewChild, ViewContainerRef, Type, EventEmitter } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LazyLoaderService } from '@core/lazy';

export interface CustomDialogParams {
    submitLabel?: string;
    submitIcon?: string;
    cancelLabel?: string;
    pending?: boolean;
    title?: string;
    height?: number;
    width?: number;
    component?: Type<unknown>;
    inputs?: Record<string, unknown>;
    hideCloseButton?: boolean;
    outputs?: Record<string, (value: unknown) => void>;
}

interface HasOnSend {
    onSend?: () => void;
}

@Component({
    selector: 'app-custom-dialog',
    templateUrl: './custom-dialog.component.html',
    styleUrls: ['./custom-dialog.component.scss'],
    standalone: false
})
export class CustomDialogComponent {
    @ViewChild('target', { read: ViewContainerRef, static: false })
    private viewContainerRef!: ViewContainerRef;

    @ViewChild('loader', { read: TemplateRef, static: true })
    private loaderTemplateRef!: TemplateRef<unknown>;

    public componentRef!: ComponentRef<unknown>;

    public hideCloseButton = false;

    public title?: string;

    public width: number;

    public height: number;

    public submitLabel: string;

    public submitIcon: string;

    public cancelLabel: string;

    public pending: boolean;

    constructor(
        private readonly lazyLoader: LazyLoaderService,
        @Inject(MAT_DIALOG_DATA) public data: CustomDialogParams,
        public dialogRef: MatDialogRef<CustomDialogComponent>
    ) {
        this.title = data.title ?? '';
        this.width = data.width ?? 400;
        this.height = data.height ?? 400;
        this.submitLabel = data.submitLabel ?? 'Wyślij';
        this.cancelLabel = data.cancelLabel ?? 'Anuluj';
        this.pending = Boolean(data.pending);
        this.submitIcon = data.submitIcon ?? 'send';
    }

    public onSubmit(): void {
        const instance = this.componentRef?.instance as HasOnSend;

        if (instance && typeof instance.onSend === 'function') {
            instance.onSend();
        }
    }

    public onCancel(): void {
        this.data.outputs?.onCancel?.(null) ?? this.dialogRef.close();
    }

    public ngAfterViewInit(): void {
        this.viewContainerRef.createEmbeddedView(this.loaderTemplateRef);

        if (this.data.component) {
            this.viewContainerRef.clear();
            this.componentRef = this.viewContainerRef.createComponent(this.data.component);
            this.hideCloseButton = this.data.hideCloseButton ?? false;

            if (this.data.inputs) {
                Object.entries(this.data.inputs).forEach(([key, value]) => {
                    this.componentRef.setInput(key, value);
                });
            }

            if (this.data.outputs) {
                Object.entries(this.data.outputs).forEach(([outputName, callback]) => {
                    const instance = this.componentRef.instance as { [key: string]: EventEmitter<unknown> | undefined; };
                    const emitter = instance[outputName];

                    if (emitter && typeof emitter.subscribe === 'function') {
                        emitter.subscribe(callback);
                    }
                });
            }
        } else {
            throw new Error('You HAVE TO provide module or component param');
        }

        this.componentRef.changeDetectorRef.detectChanges();
    }

    public setDynamicInput(key: string, value: unknown): void {
        if (key === 'pending') {
            this.pending = Boolean(value);
        }

        if (this.componentRef) {
            this.componentRef.setInput(key, value);
            this.componentRef.changeDetectorRef.detectChanges();
        }
    }
}
